import type { ListingCardData } from "#/components/listing-card";
import { CONDITION_SLIDER } from "#/lib/constants";

// --- URL search param parse helpers ---

export function toArray(val: unknown): string[] {
	if (!val) return [];
	if (Array.isArray(val)) return val.filter((v) => typeof v === "string");
	if (typeof val === "string") return [val];
	return [];
}

export function toInt(val: unknown): number | "" {
	const n = Number(val);
	return val !== "" && val != null && !Number.isNaN(n) ? n : "";
}

// --- Stability presets ---

export type StabilityPreset = "understable" | "neutral" | "overstable";

export const STABILITY_PRESETS: { value: StabilityPreset; label: string }[] = [
	{ value: "understable", label: "US" },
	{ value: "neutral", label: "Neutral" },
	{ value: "overstable", label: "OS" },
];

export type FilterState = {
	q: string;
	discClass: string[];
	condition: string[];
	color: string[];
	shipsTo: string[];
	country: string[];
	plasticType: string[];
	priceMin: number | "";
	priceMax: number | "";
	weightMin: number | "";
	weightMax: number | "";
	discModelIds: string[];
	manufacturerIds: string[];
	speedMin: number | "";
	speedMax: number | "";
	glideMin: number | "";
	glideMax: number | "";
	turnMin: number | "";
	turnMax: number | "";
	fadeMin: number | "";
	fadeMax: number | "";
	distanceKm: number | "";
};

export const EMPTY_FILTERS: FilterState = {
	q: "",
	discClass: [],
	condition: [],
	color: [],
	shipsTo: [],
	country: [],
	plasticType: [],
	priceMin: "",
	priceMax: "",
	weightMin: "",
	weightMax: "",
	discModelIds: [],
	manufacturerIds: [],
	speedMin: "",
	speedMax: "",
	glideMin: "",
	glideMax: "",
	turnMin: "",
	turnMax: "",
	fadeMin: "",
	fadeMax: "",
	distanceKm: "",
};

export function countActiveFilters(filters: FilterState): number {
	let count = 0;
	count += filters.discClass.length;
	if (filters.condition.length > 0) count += 1;
	count += filters.color.length;
	count += filters.shipsTo.length;
	count += filters.country.length;
	count += filters.plasticType.length;
	if (filters.priceMin !== "" || filters.priceMax !== "") count += 1;
	if (filters.weightMin !== "" || filters.weightMax !== "") count += 1;
	count += filters.discModelIds.length;
	count += filters.manufacturerIds.length;
	if (
		filters.speedMin !== "" ||
		filters.speedMax !== "" ||
		filters.glideMin !== "" ||
		filters.glideMax !== "" ||
		filters.turnMin !== "" ||
		filters.turnMax !== "" ||
		filters.fadeMin !== "" ||
		filters.fadeMax !== ""
	)
		count += 1;
	if (filters.distanceKm !== "") count += 1;
	return count;
}

export function hasActiveFilters(filters: FilterState): boolean {
	return (
		!!filters.q ||
		filters.discClass.length > 0 ||
		filters.condition.length > 0 ||
		filters.color.length > 0 ||
		filters.shipsTo.length > 0 ||
		filters.country.length > 0 ||
		filters.plasticType.length > 0 ||
		filters.priceMin !== "" ||
		filters.priceMax !== "" ||
		filters.weightMin !== "" ||
		filters.weightMax !== "" ||
		filters.discModelIds.length > 0 ||
		filters.manufacturerIds.length > 0 ||
		filters.speedMin !== "" ||
		filters.speedMax !== "" ||
		filters.glideMin !== "" ||
		filters.glideMax !== "" ||
		filters.turnMin !== "" ||
		filters.turnMax !== "" ||
		filters.fadeMin !== "" ||
		filters.fadeMax !== "" ||
		filters.distanceKm !== ""
	);
}

export function filterStateToQueryArgs(filters: FilterState) {
	const num = (v: number | "") => (v !== "" ? v : undefined);
	const arr = (v: string[]) => (v.length ? v : undefined);
	return {
		q: filters.q || undefined,
		condition: arr(filters.condition),
		color: arr(filters.color),
		shipsTo: arr(filters.shipsTo),
		country: arr(filters.country),
		plasticType: arr(filters.plasticType),
		priceMin: num(filters.priceMin),
		priceMax: num(filters.priceMax),
		weightMin: num(filters.weightMin),
		weightMax: num(filters.weightMax),
		discModelIds: arr(filters.discModelIds),
		discClass: arr(filters.discClass),
		manufacturerIds: arr(filters.manufacturerIds),
		speedMin: num(filters.speedMin),
		speedMax: num(filters.speedMax),
		glideMin: num(filters.glideMin),
		glideMax: num(filters.glideMax),
		turnMin: num(filters.turnMin),
		turnMax: num(filters.turnMax),
		fadeMin: num(filters.fadeMin),
		fadeMax: num(filters.fadeMax),
	};
}

export function getPresetValues(preset: StabilityPreset): Partial<FilterState> {
	if (preset === "understable")
		return { turnMin: "", turnMax: -2, fadeMin: "", fadeMax: 2 };
	if (preset === "neutral")
		return { turnMin: -2, turnMax: 0, fadeMin: 1, fadeMax: 3 };
	return { turnMin: 0, turnMax: "", fadeMin: 3, fadeMax: "" };
}

export function detectPreset(filters: FilterState): StabilityPreset | null {
	if (
		filters.turnMax === -2 &&
		filters.fadeMax === 2 &&
		filters.turnMin === "" &&
		filters.fadeMin === ""
	)
		return "understable";
	if (
		filters.turnMin === -2 &&
		filters.turnMax === 0 &&
		filters.fadeMin === 1 &&
		filters.fadeMax === 3
	)
		return "neutral";
	if (
		filters.turnMin === 0 &&
		filters.turnMax === "" &&
		filters.fadeMin === 3 &&
		filters.fadeMax === ""
	)
		return "overstable";
	return null;
}

// --- Filter pill label generators ---

export function conditionRangeLabel(condition: string[]): string {
	if (condition.length === 0) return "";
	const indices = condition
		.map((c) => CONDITION_SLIDER.findIndex((s) => s.value === c))
		.filter((i) => i >= 0)
		.sort((a, b) => a - b);
	const lo = CONDITION_SLIDER[indices[0]]?.label ?? condition[0];
	const hi =
		CONDITION_SLIDER[indices[indices.length - 1]]?.label ??
		condition[condition.length - 1];
	return lo === hi ? `Condition: ${lo}` : `Condition: ${lo} – ${hi}`;
}

export function priceRangeLabel(min: number | "", max: number | ""): string {
	if (min !== "" && max !== "") return `€${min} – €${max}`;
	if (min !== "") return `≥ €${min}`;
	return `≤ €${max}`;
}

export function weightRangeLabel(min: number | "", max: number | ""): string {
	if (min !== "" && max !== "") return `${min}g – ${max}g`;
	if (min !== "") return `≥ ${min}g`;
	return `≤ ${max}g`;
}

export function flightRangeLabel(
	label: string,
	min: number | "",
	max: number | "",
	fmt?: (n: number) => string,
): string {
	const f = fmt ?? String;
	if (min !== "" && max !== "") return `${label} ${f(min)}–${f(max)}`;
	if (min !== "") return `${label} ≥${f(min)}`;
	return `${label} ≤${f(max as number)}`;
}

export function signedVal(n: number): string {
	return n > 0 ? `+${n}` : String(n);
}

export function applySort(
	listings: ListingCardData[],
	sort: "newest" | "price-asc" | "price-desc",
): ListingCardData[] {
	const copy = [...listings];
	if (sort === "price-asc") return copy.sort((a, b) => a.price - b.price);
	if (sort === "price-desc") return copy.sort((a, b) => b.price - a.price);
	return copy;
}
